/**
 * Lesson View — תצוגת שיעור
 *
 * Renders the pre-authored lesson plus its glossary. Costs nothing: this is static
 * content from the DB, not a model call.
 *
 * `lessonHtml` is sanitized SERVER-SIDE on save (sanitizeLessonHtml), which strips
 * scripts/handlers/links and disallows h1/h2 — so the page keeps ownership of the
 * top two heading levels and authored headings start at h3.
 */

import { BookMarked } from 'lucide-react'
import type { LearningKeyTerm } from '../../types'

interface LessonViewProps {
  lessonHtml: string
  keyTerms: LearningKeyTerm[]
}

export default function LessonView({ lessonHtml, keyTerms }: LessonViewProps) {
  return (
    <div>
      <section aria-labelledby="lesson-body-heading">
        {/*
          The authored content starts at <h3>, and the page owns <h1>. Without an
          <h2> in between the document would skip a heading level, which axe flags
          and which makes the lesson harder to navigate by headings. This one is
          visually hidden but present for assistive tech.
        */}
        <h2 id="lesson-body-heading" className="sr-only">
          תוכן השיעור
        </h2>
        <div
          className="lesson-content max-w-none text-gray-800 leading-relaxed"
          // Safe: sanitized on the server before it was ever stored.
          dangerouslySetInnerHTML={{ __html: lessonHtml }}
        />
      </section>

      {keyTerms.length > 0 && (
        <section className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-5" aria-labelledby="key-terms-heading">
          <h2 id="key-terms-heading" className="flex items-center gap-2 text-base font-bold text-gray-900">
            <BookMarked size={18} aria-hidden="true" />
            מושגי מפתח
          </h2>
          <dl className="mt-3 space-y-3">
            {keyTerms.map((t) => (
              <div key={t.term}>
                <dt className="text-sm font-semibold text-gray-900">{t.term}</dt>
                <dd className="text-sm text-gray-700">{t.definition}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  )
}
